import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {animate, state, style, transition, trigger} from "@angular/animations";
import {take} from "rxjs";
import {Router} from "@angular/router";
import {DETAILS, ORGANISER_EDIT, ORGANISER_LIST} from 'src/app/app-routing.module';
import {FormControl, FormGroup} from '@angular/forms';
import {TranslocoService} from '@ngneat/transloco';
import {OrganiserFormMode} from "../organiser-details.component";
import {AbstractComponent} from "@components";
import {Country, Organiser, TOAST_STATE} from "@model";
import {
  AuthenticationService,
  LoaderService,
  OrganiserService,
  RequestService,
  ToastService
} from "@services";
import {alphabetical, email, max_length_255, max_length_50, required} from "@utility";

@Component({
  selector: 'app-organiser-basic-data',
  templateUrl: './organiser-basic-data.component.html',
  styleUrls: ['./organiser-basic-data.component.scss'],
  animations: [
    trigger('fadeInAnimation', [
      state('in', style({opacity: 1})),
      transition(':enter', [
        style({opacity: 0}),
        animate(1600)
      ])
    ])]
})
export class OrganiserBasicDataComponent extends AbstractComponent implements OnInit, OnChanges {
  @Input() countries: Country[] = [];
  @Input() organiserFormMode: OrganiserFormMode | undefined;
  @Input() currentOrganiser: Organiser | undefined;


  public isNewOrganizerCollapsed: boolean = false;
  public isDragAndDropCollapsed: boolean = true;
  public isOrganizerLinksCollapsed: boolean = true;
  public isEditorCollapsed: boolean = true;

  inputForm = new FormGroup({
    userId: new FormControl(),
    name: new FormControl(),
    fullName: new FormControl(),
    website: new FormControl(),
    email: new FormControl(),
    phone: new FormControl(),
    address: new FormControl(),
    place: new FormControl(),
    country: new FormControl(),
    image: new FormControl(),
    info: new FormControl(),
    zip: new FormControl(),
    organiserLinks: new FormGroup({
      facebook: new FormControl(),
      twitter: new FormControl(),
      googleplus: new FormControl(),
      picasa: new FormControl(),
      flickr: new FormControl(),
      youtube: new FormControl(),
      vimeo: new FormControl(),
    }),
  });

  constructor(
    private organiserService: OrganiserService, private toastService: ToastService, private router: Router,
    private translocoService: TranslocoService, public authenticationService: AuthenticationService,
    private requestService: RequestService, private loaderService:LoaderService) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.setFormValues();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.setFormValues();
  }

  private initForm() {
    this.inputForm.controls.name.addValidators([required, max_length_50]);
    this.inputForm.controls.fullName.addValidators([required, max_length_255]);
    this.inputForm.controls.website.addValidators([required, max_length_255]);
    this.inputForm.controls.email.addValidators([required, email]);
    this.inputForm.controls.phone.addValidators([required]);
    this.inputForm.controls.address.addValidators([required, max_length_255]);
    this.inputForm.controls.place.addValidators([required, max_length_255, alphabetical]);
    this.inputForm.controls.zip.addValidators([required, max_length_255]);
    this.inputForm.controls.country.addValidators([required]);
    this.inputForm.updateValueAndValidity();
  }

  private setFormValues() {
    if (this.currentOrganiser) {
      let formValue = {...this.currentOrganiser}
      this.inputForm.patchValue(formValue);
      this.inputForm.controls.country.setValue(this.getCountry(this.currentOrganiser?.country?.shortCode)?.id)
      this.subscriptions.push(
        this.inputForm.valueChanges.pipe(
          take(1)).subscribe(() => {
            this.inputForm.markAsTouched();
          }
        )
      );
    }
    this.inputForm.markAsUntouched();
  }

  getCountryString(countryCode: number): string {
    let country: Country | undefined = this.countries.find((country: Country) => {
      return country.id === countryCode;
    });
    return country?.name?.toUpperCase() ?? "";
  }

  getFormTitle(): string {
    switch (this.organiserFormMode) {
      case OrganiserFormMode.EDIT:
        return this.translocoService.translate("organiser.form.header.edit");
      case OrganiserFormMode.NEW:
        return this.translocoService.translate("organiser.form.header.new");
      case OrganiserFormMode.PROFILE:
        return this.translocoService.translate("organiser.form.header.edit");
      default:
        return "";
    }
  }

  onCancel(): void {
    if (this.organiserFormMode === OrganiserFormMode.PROFILE) {
      this.organiserService.getLoggedInOrganiser();
    } else {
      this.router.navigate([ORGANISER_LIST]);
    }
  }

  onSave(): void {
    if (!this.inputForm.valid || !this.authenticationService.isAdminstrator()) {
      this.markAllAsTouched(this.inputForm);
      return;
    } else {
      const organiserDetails: Organiser = this.getOrganiserFields();

      const onSuccess = (result: Organiser) => {
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("app.common.success.organiser.edit"));
        if(!organiserDetails.id) {
          this.router.navigate([ORGANISER_EDIT], {queryParams: {organiserId: result.id}});
        }
      }
      const onError = () => {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("app.common.server.error"));
      }
      this.organiserService.updateOrganiser(organiserDetails, onSuccess, onError);
    }
  }

  imageChange(image: string | undefined) {
    this.inputForm.controls.image.setValue(image);
  }

  getCountry(shortCode?: string): Country {
    return this.countries.filter(country => country.shortCode?.toUpperCase() === shortCode?.toUpperCase())[0] ?? undefined;
  }

  onDescriptionChangeEvent(content: string | undefined) {
    this.inputForm.controls.info.setValue(content);
  }


  getOrganiserFields(): Organiser {
    this.checkValidators(this.inputForm);
    let organiserDetails: Organiser = {...this.currentOrganiser}
    organiserDetails.address = this.inputForm.value.address;
    organiserDetails.name = this.inputForm.value.name;
    organiserDetails.fullName = this.inputForm.value.fullName;
    organiserDetails.website = this.inputForm.value.website;
    organiserDetails.email = this.inputForm.value.email;
    organiserDetails.zip = this.inputForm.value.zip;
    organiserDetails.place = this.inputForm.value.place;
    organiserDetails.phone = this.inputForm.controls.phone.value;
    organiserDetails.image = this.inputForm.controls.image.value;
    organiserDetails.organiserLinks!.facebook = this.inputForm.value.organiserLinks?.facebook;
    organiserDetails.organiserLinks!.twitter = this.inputForm.value.organiserLinks?.twitter;
    organiserDetails.organiserLinks!.googleplus = this.inputForm.value.organiserLinks?.googleplus;
    organiserDetails.organiserLinks!.picasa = this.inputForm.value.organiserLinks?.picasa;
    organiserDetails.organiserLinks!.flickr = this.inputForm.value.organiserLinks?.flickr;
    organiserDetails.organiserLinks!.youtube = this.inputForm.value.organiserLinks?.youtube;
    organiserDetails.organiserLinks!.vimeo = this.inputForm.value.organiserLinks?.vimeo;
    let country: Country | undefined = this.countries.find((country: Country) => country.id === this.inputForm.value.country);
    if (country) {
      organiserDetails.country = {
        countryId: country.id,
        shortCode: country.shortCode
      };
    } else {
      organiserDetails.country = undefined;
    }
    organiserDetails.info = this.inputForm.value.info;
    return organiserDetails;
  }
}



